/**
 * The presentational body of the pool Rules content, shared by both web
 * mounts: the pool home `Rules` card and the pool settings `contest-rules`
 * slot. `.agents/design-prompts/27-pool-settings.md` section 27.1 is the
 * source of this layout.
 *
 * This component renders and decides nothing on its own: it draws a
 * resolved `typePoolRulesState` and calls no query, reads no `poolId`, and
 * touches no error bus. It renders no `Card` of its own; both mounts supply
 * their own shell.
 */
import { cn, InlineBanner, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  POOL_RULES_CONTEST_HEADING,
  POOL_RULES_FAILED_MESSAGE,
  POOL_RULES_LOADING_LABEL,
  POOL_RULES_RETRY_LABEL,
  type typePoolRulesState,
} from "./poolRulesState";

export type typePoolRulesBodyProps = {
  state: typePoolRulesState;
  /** Visually clips the prose. The pool home passes this while collapsed. */
  doClamp?: boolean | undefined;
  /** Called by the failed banner's Try again action. */
  onRetry: () => void;
};

export const PoolRulesBody = ({
  state,
  doClamp,
  onRetry,
}: typePoolRulesBodyProps): ReactNode => {
  if (state.phase === "loading") {
    return (
      <div data-testid="pool-rules-loading">
        <Skeleton shape="text" lines={3} label={POOL_RULES_LOADING_LABEL} />
      </div>
    );
  }

  if (state.phase === "failed") {
    return (
      <div data-testid="pool-rules-failed">
        <InlineBanner
          variant="warning"
          message={POOL_RULES_FAILED_MESSAGE}
          action={{ label: POOL_RULES_RETRY_LABEL, onAction: onRetry }}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        doClamp && "max-h-40 overflow-hidden",
      )}
      data-testid="pool-rules-body"
    >
      {state.model.commissionerPreamble !== null ? (
        <p
          className="whitespace-pre-line text-body text-ink"
          data-testid="pool-rules-preamble"
        >
          {state.model.commissionerPreamble}
        </p>
      ) : null}
      <p
        className="whitespace-pre-line text-body text-ink-secondary"
        data-testid="pool-rules-summary"
      >
        {state.model.autoGeneratedSummary}
      </p>
      {state.model.contestSection !== null ? (
        <>
          <h4 className="text-body text-ink">{POOL_RULES_CONTEST_HEADING}</h4>
          {state.model.contestSection.commissionerPreamble !== null ? (
            <p className="whitespace-pre-line text-body text-ink">
              {state.model.contestSection.commissionerPreamble}
            </p>
          ) : null}
          <p
            className="whitespace-pre-line text-body text-ink-secondary"
            data-testid="pool-rules-contest"
          >
            {state.model.contestSection.autoGeneratedText}
          </p>
        </>
      ) : null}
    </div>
  );
};
